import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import {
  Shield, BookOpen, Loader2, CheckCircle2, XCircle, Clock,
  Eye, Trash2, ToggleLeft, ToggleRight, Wand2, ArrowLeft, AlertCircle,
  Lock, KeyRound, Plus, Pencil, Users, Star, MessageSquare,
  ChevronDown, ChevronUp, Award, TrendingUp, Layers, Newspaper, Briefcase, Layout, Building2,
  CreditCard, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import AdminCatalogTab from '../components/AdminCatalogTab';
import AdminBlogTab from '../components/AdminBlogTab';
import AdminJobOffersTab from '../components/AdminJobOffersTab';
import AdminSiteContentTab from '../components/AdminSiteContentTab';
import AdminEspaceEmploiTab from '../components/AdminEspaceEmploiTab';
import AdminPaymentsTab from '../components/AdminPaymentsTab';
import AdminBookingsTab from '../components/AdminBookingsTab';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EMOJI_MAP = { 1: '\u{1F621}', 2: '\u{1F615}', 3: '\u{1F610}', 4: '\u{1F642}', 5: '\u{1F929}' };

const AdminPage = () => {
  const { user, token, loading: authLoading, login } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [pollingId, setPollingId] = useState(null);
  const [slidesPerModule, setSlidesPerModule] = useState(4);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({ title: '', category: '', level: 'Intermédiaire', duration: '8h', description: '' });

  // Tabs
  const [activeTab, setActiveTab] = useState('courses');

  // Admin auth gate
  const [adminVerified, setAdminVerified] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Learner tracking
  const [learners, setLearners] = useState([]);
  const [learnersLoading, setLearnersLoading] = useState(false);
  const [expandedLearner, setExpandedLearner] = useState(null);
  const [learnerFilter, setLearnerFilter] = useState('all');

  // Évaluations
  const [evaluations, setEvaluations] = useState([]);
  const [evalStats, setEvalStats] = useState(null);
  const [evalsLoading, setEvalsLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchCourses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/courses`, { headers });
      setCourses(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Acces reserve aux administrateurs");
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchCourses();
  }, [token, fetchCourses]);

  // Poll generating courses
  useEffect(() => {
    const generatingCourse = courses.find(c => c.status === 'generating');
    if (generatingCourse && generatingCourse.id) {
      const interval = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/admin/courses/${generatingCourse.id}/status`, { headers });
          if (res.data.status !== 'generating') {
            clearInterval(interval);
            fetchCourses();
            if (res.data.status === 'ready') {
              toast.success(`Formation "${generatingCourse.title}" générée !`);
            } else if (res.data.status === 'error') {
              toast.error(`Erreur: ${res.data.generation_step}`);
            }
            setGenerating(null);
          } else {
            setCourses(prev => prev.map(c =>
              c.id === generatingCourse.id ? { ...c, generation_step: res.data.generation_step } : c
            ));
          }
        } catch {}
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [courses, token]);

  const handleGenerate = async (catalogId) => {
    setGenerating(catalogId);
    try {
      const res = await axios.post(`${API}/admin/courses/generate`, { catalog_id: catalogId, slides_per_module: slidesPerModule }, { headers });
      toast.success("Génération démarree...");
      setCourses(prev => prev.map(c =>
        c.catalog_id === catalogId ? { ...c, id: res.data.id, status: 'generating', generation_step: 'Initialisation...' } : c
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de generation");
      setGenerating(null);
    }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/courses/create`, createForm, { headers });
      toast.success("Formation créée !");
      setShowCreateForm(false);
      setCreateForm({ title: '', category: '', level: 'Intermédiaire', duration: '8h', description: '' });
      fetchCourses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handlePublish = async (courseId) => {
    try {
      const res = await axios.post(`${API}/admin/courses/${courseId}/publish`, {}, { headers });
      toast.success(res.data.published ? "Formation publiée" : "Formation dépubliée");
      fetchCourses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (courseId, title) => {
    if (!window.confirm(`Supprimer "${title}" ? Cette action est irreversible.`)) return;
    try {
      await axios.delete(`${API}/admin/courses/${courseId}`, { headers });
      toast.success("Formation supprimee");
      fetchCourses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const fetchLearners = useCallback(async () => {
    setLearnersLoading(true);
    try {
      const res = await axios.get(`${API}/admin/learners`, { headers });
      setLearners(res.data);
    } catch {
      toast.error("Erreur chargement des apprenants");
    } finally {
      setLearnersLoading(false);
    }
  }, [token]);

  const fetchEvaluations = useCallback(async () => {
    setEvalsLoading(true);
    try {
      const [evalsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/evaluations`, { headers }),
        axios.get(`${API}/admin/evaluations/stats`, { headers })
      ]);
      setEvaluations(evalsRes.data);
      setEvalStats(statsRes.data);
    } catch {
      toast.error("Erreur chargement des evaluations");
    } finally {
      setEvalsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (adminVerified && activeTab === 'learners') fetchLearners();
    if (adminVerified && activeTab === 'evaluations') fetchEvaluations();
  }, [activeTab, adminVerified]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  // Admin login gate - always require explicit admin login
  if (!adminVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 pt-0" data-testid="admin-login-gate">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-teal-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Administration</h2>
              <p className="text-sm text-slate-500 mt-1">Connexion administrateur requise</p>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              setLoginLoading(true);
              setLoginError('');
              try {
                const u = await login(loginForm.email, loginForm.password);
                if (!u.is_admin) {
                  setLoginError('Ce compte n\'a pas les droits administrateur.');
                  setLoginLoading(false);
                  return;
                }
                setAdminVerified(true);
              } catch (err) {
                setLoginError(err.response?.data?.detail || 'Email ou mot de passe incorrect');
              } finally {
                setLoginLoading(false);
              }
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email admin</label>
                <Input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                  placeholder="admin@aerh.pf"
                  required
                  data-testid="admin-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Mot de passe</label>
                <Input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  placeholder="Mot de passe"
                  required
                  data-testid="admin-password-input"
                />
              </div>
              {loginError && (
                <div className="text-sm text-red-600 bg-red-50 p-2 rounded-lg flex items-center gap-2" data-testid="admin-login-error">
                  <Lock className="w-4 h-4 shrink-0" />
                  {loginError}
                </div>
              )}
              <Button type="submit" disabled={loginLoading} className="w-full bg-teal-600 hover:bg-teal-700 text-white" data-testid="admin-login-btn">
                {loginLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                Connexion admin
              </Button>
            </form>
            <button onClick={() => navigate('/')} className="w-full text-center text-sm text-slate-400 hover:text-slate-600 mt-4">
              Retour au site
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (!user || !user.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 pt-24">
        <div className="text-center">
          <Lock className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Acces refuse</h2>
          <p className="text-slate-500 mb-4">Ce compte n'a pas les droits administrateur.</p>
          <Button onClick={() => navigate('/')} variant="outline">
            Retour au site
          </Button>
        </div>
      </div>
    );
  }

  const statusIcon = (status) => {
    switch (status) {
      case 'ready': return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      case 'generating': return <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />;
      case 'error': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  const statusLabel = (status) => {
    switch (status) {
      case 'ready': return 'Pret';
      case 'generating': return 'En cours...';
      case 'error': return 'Erreur';
      default: return 'Non généré';
    }
  };

  const categories = [...new Set(courses.map(c => c.category))];

  const filteredLearners = learnerFilter === 'all'
    ? learners
    : learners.filter(l => {
        if (learnerFilter === 'active') return l.in_progress_courses > 0;
        if (learnerFilter === 'passed') return l.passed_courses > 0;
        if (learnerFilter === 'failed') return l.failed_courses > 0;
        return true;
      });

  const renderEmojiScore = (score) => {
    const rounded = Math.round(score);
    return <span className="text-lg">{EMOJI_MAP[rounded] || EMOJI_MAP[3]}</span>;
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-16" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <button onClick={() => navigate('/')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-teal-600 mb-2">
              <ArrowLeft className="w-3.5 h-3.5" /> Retour au site
            </button>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Shield className="w-6 h-6 text-teal-600" />
              Administration
            </h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl border border-slate-200 p-1 mb-6" data-testid="admin-tabs">
          {[
            { id: 'courses', label: 'Formations E-Learning', icon: BookOpen, count: courses.filter(c => c.status === 'ready').length + '/' + courses.length },
            { id: 'catalog', label: 'Catalogue', icon: Layers, count: '' },
            { id: 'learners', label: 'Suivi apprenants', icon: Users, count: learners.length || '' },
            { id: 'evaluations', label: 'Évaluations', icon: Star, count: evaluations.length || '' },
            { id: 'blog', label: 'Blog', icon: Newspaper, count: '' },
            { id: 'job-offers', label: 'Offres emploi', icon: Briefcase, count: '' },
            { id: 'espace-emploi', label: 'Espace Emploi', icon: Building2, count: '' },
            { id: 'payments', label: 'Paiements', icon: CreditCard, count: '' },
            { id: 'bookings', label: 'Réservations', icon: Calendar, count: '' },
            { id: 'site-content', label: 'Contenu', icon: Layout, count: '' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex-1 justify-center ${
                activeTab === tab.id
                  ? 'bg-teal-600 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count && <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-teal-500' : 'bg-slate-100'}`}>{tab.count}</span>}
            </button>
          ))}
        </div>

        {/* TAB: Formations (existing) */}
        {activeTab === 'courses' && (
          <>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-500">{courses.filter(c => c.status === 'ready').length}/{courses.length} formations générées</p>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
                  <label className="text-xs text-slate-500 whitespace-nowrap">Slides/module:</label>
                  <select value={slidesPerModule} onChange={(e) => setSlidesPerModule(parseInt(e.target.value))} className="text-sm border-0 bg-transparent font-medium" data-testid="select-slides-per-module">
                    {[3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <Button onClick={() => setShowCreateForm(!showCreateForm)} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="btn-show-create">
                  <Plus className="w-4 h-4 mr-1" /> Nouvelle formation
                </Button>
              </div>
            </div>

            {showCreateForm && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl border-2 border-teal-200 p-5 mb-6" data-testid="create-course-form">
                <h3 className="font-bold text-slate-900 mb-3">Créer une nouvelle formation</h3>
                <form onSubmit={handleCreateCourse} className="grid md:grid-cols-2 gap-3">
                  <input value={createForm.title} onChange={(e) => setCreateForm({...createForm, title: e.target.value})} placeholder="Titre de la formation" required className="col-span-2 h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="input-new-title" />
                  <input value={createForm.category} onChange={(e) => setCreateForm({...createForm, category: e.target.value})} placeholder="Categorie" required className="h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="input-new-category" />
                  <select value={createForm.level} onChange={(e) => setCreateForm({...createForm, level: e.target.value})} className="h-10 rounded-md border border-slate-200 px-3 text-sm">
                    <option value="Débutant">Débutant</option>
                    <option value="Intermédiaire">Intermédiaire</option>
                    <option value="Avancé">Avancé</option>
                  </select>
                  <input value={createForm.duration} onChange={(e) => setCreateForm({...createForm, duration: e.target.value})} placeholder="Duree (ex: 8h)" className="h-10 rounded-md border border-slate-200 px-3 text-sm" />
                  <textarea value={createForm.description} onChange={(e) => setCreateForm({...createForm, description: e.target.value})} placeholder="Description" className="col-span-2 rounded-md border border-slate-200 px-3 py-2 text-sm" rows={2} />
                  <div className="col-span-2 flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>Annuler</Button>
                    <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="btn-create-course">Créer</Button>
                  </div>
                </form>
              </motion.div>
            )}

            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
              </div>
            ) : (
              categories.map(cat => (
                <div key={cat} className="mb-8" data-testid={`category-${cat}`}>
                  <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-teal-600" />
                    {cat}
                    <span className="text-sm font-normal text-slate-400">
                      ({courses.filter(c => c.category === cat).length})
                    </span>
                  </h2>
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left p-3 font-semibold text-slate-600">Formation</th>
                          <th className="text-left p-3 font-semibold text-slate-600 w-24">Niveau</th>
                          <th className="text-left p-3 font-semibold text-slate-600 w-28">Statut</th>
                          <th className="text-left p-3 font-semibold text-slate-600 w-20">Slides</th>
                          <th className="text-left p-3 font-semibold text-slate-600 w-20">Publie</th>
                          <th className="text-right p-3 font-semibold text-slate-600 w-44">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {courses.filter(c => c.category === cat).map((course) => (
                          <tr key={course.catalog_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50" data-testid={`course-row-${course.catalog_id}`}>
                            <td className="p-3">
                              <p className="font-medium text-slate-900">{course.title}</p>
                              {course.status === 'generating' && course.generation_step && (
                                <p className="text-xs text-amber-600 mt-0.5 flex items-center gap-1">
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  {course.generation_step}
                                </p>
                              )}
                              {course.status === 'error' && course.generation_step && (
                                <p className="text-xs text-red-600 mt-0.5 flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" />
                                  {course.generation_step}
                                </p>
                              )}
                            </td>
                            <td className="p-3">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                course.level === 'Débutant' ? 'bg-green-100 text-green-700' :
                                course.level === 'Intermédiaire' ? 'bg-blue-100 text-blue-700' :
                                'bg-orange-100 text-orange-700'
                              }`}>
                                {course.level}
                              </span>
                            </td>
                            <td className="p-3">
                              <span className="flex items-center gap-1.5">
                                {statusIcon(course.status)}
                                <span className="text-xs">{statusLabel(course.status)}</span>
                              </span>
                            </td>
                            <td className="p-3 text-slate-600">{course.slide_count || '-'}</td>
                            <td className="p-3">
                              {course.status === 'ready' && (
                                <button onClick={() => handlePublish(course.id)} data-testid={`btn-publish-${course.catalog_id}`}>
                                  {course.published ?
                                    <ToggleRight className="w-6 h-6 text-teal-600" /> :
                                    <ToggleLeft className="w-6 h-6 text-slate-400" />
                                  }
                                </button>
                              )}
                            </td>
                            <td className="p-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {course.status === 'not_generated' || course.status === 'error' ? (
                                  <Button size="sm" onClick={() => handleGenerate(course.catalog_id)} disabled={generating === course.catalog_id} className="bg-teal-600 hover:bg-teal-700 text-white text-xs h-8" data-testid={`btn-generate-${course.catalog_id}`}>
                                    {generating === course.catalog_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}
                                    Générer
                                  </Button>
                                ) : course.status === 'ready' ? (
                                  <>
                                    <Button size="sm" variant="outline" onClick={() => navigate(`/admin/editor/${course.id}`)} className="text-xs h-8" data-testid={`btn-edit-${course.catalog_id}`}>
                                      <Pencil className="w-3 h-3 mr-1" /> Editer
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => navigate(`/cours/${course.catalog_id}`)} className="text-xs h-8" data-testid={`btn-preview-${course.catalog_id}`}>
                                      <Eye className="w-3 h-3 mr-1" /> Voir
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => handleGenerate(course.catalog_id)} className="text-xs h-8 text-amber-600 border-amber-300 hover:bg-amber-50" data-testid={`btn-regen-${course.catalog_id}`}>
                                      <Wand2 className="w-3 h-3 mr-1" /> Régénérer
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => handleDelete(course.id, course.title)} className="text-xs h-8 text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`btn-delete-${course.catalog_id}`}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {/* TAB: Catalogue (Presential) */}
        {activeTab === 'catalog' && (
          <AdminCatalogTab token={token} />
        )}

        {/* TAB: Suivi Apprenants */}
        {activeTab === 'learners' && (
          <div data-testid="learners-tab">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1">
                {[
                  { id: 'all', label: 'Tous' },
                  { id: 'active', label: 'En cours' },
                  { id: 'passed', label: 'Reussis' },
                  { id: 'failed', label: 'Echoues' }
                ].map(f => (
                  <button
                    key={f.id}
                    onClick={() => setLearnerFilter(f.id)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${learnerFilter === f.id ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                    data-testid={`filter-${f.id}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <span className="text-sm text-slate-500">{filteredLearners.length} apprenant{filteredLearners.length !== 1 ? 's' : ''}</span>
            </div>

            {learnersLoading ? (
              <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
            ) : filteredLearners.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Aucun apprenant trouve</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredLearners.map(learner => (
                  <div key={learner.user_id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`learner-${learner.user_id}`}>
                    <button
                      onClick={() => setExpandedLearner(expandedLearner === learner.user_id ? null : learner.user_id)}
                      className="w-full flex items-center justify-between p-4 hover:bg-slate-50/50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold text-sm">
                          {learner.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900 text-sm">{learner.name}</p>
                          <p className="text-xs text-slate-500">{learner.email}{learner.company ? ` - ${learner.company}` : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex gap-3 text-xs">
                          {learner.passed_courses > 0 && (
                            <span className="flex items-center gap-1 text-green-600"><CheckCircle2 className="w-3.5 h-3.5" />{learner.passed_courses} reussi{learner.passed_courses > 1 ? 's' : ''}</span>
                          )}
                          {learner.in_progress_courses > 0 && (
                            <span className="flex items-center gap-1 text-amber-600"><Clock className="w-3.5 h-3.5" />{learner.in_progress_courses} en cours</span>
                          )}
                          {learner.failed_courses > 0 && (
                            <span className="flex items-center gap-1 text-red-500"><XCircle className="w-3.5 h-3.5" />{learner.failed_courses} echoue{learner.failed_courses > 1 ? 's' : ''}</span>
                          )}
                          {learner.certificates_count > 0 && (
                            <span className="flex items-center gap-1 text-teal-600"><Award className="w-3.5 h-3.5" />{learner.certificates_count} certificat{learner.certificates_count > 1 ? 's' : ''}</span>
                          )}
                        </div>
                        {expandedLearner === learner.user_id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </div>
                    </button>

                    {expandedLearner === learner.user_id && learner.courses.length > 0 && (
                      <div className="border-t border-slate-100 p-4 bg-slate-50/50">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-slate-500">
                              <th className="text-left pb-2 font-medium">Formation</th>
                              <th className="text-left pb-2 font-medium w-24">Statut</th>
                              <th className="text-left pb-2 font-medium w-20">Score</th>
                              <th className="text-left pb-2 font-medium w-20">Tentatives</th>
                              <th className="text-left pb-2 font-medium w-20">Certificat</th>
                              <th className="text-left pb-2 font-medium w-20">Évaluation</th>
                              <th className="text-left pb-2 font-medium w-28">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {learner.courses.map(c => (
                              <tr key={c.course_id} className="border-t border-slate-200/50">
                                <td className="py-2 font-medium text-slate-800">{c.course_title}</td>
                                <td className="py-2">
                                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-medium ${
                                    c.status === 'reussi' ? 'bg-green-100 text-green-700' :
                                    c.status === 'echoue' ? 'bg-red-100 text-red-700' :
                                    'bg-amber-100 text-amber-700'
                                  }`}>
                                    {c.status === 'reussi' ? 'Reussi' : c.status === 'echoue' ? 'Echoue' : 'En cours'}
                                  </span>
                                </td>
                                <td className="py-2 font-semibold">{c.overall_score > 0 ? `${c.overall_score}%` : '-'}</td>
                                <td className="py-2">{c.attempts}</td>
                                <td className="py-2">{c.has_certificate ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <span className="text-slate-300">-</span>}</td>
                                <td className="py-2">
                                  {c.evaluation_done
                                    ? <span className="flex items-center gap-1">{renderEmojiScore(c.evaluation_score)} <span className="text-slate-600">{c.evaluation_score}/5</span></span>
                                    : <span className="text-slate-300">-</span>
                                  }
                                </td>
                                <td className="py-2 text-slate-500">{c.completed_at ? new Date(c.completed_at).toLocaleDateString('fr-FR') : c.started_at ? new Date(c.started_at).toLocaleDateString('fr-FR') : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB: Évaluations */}
        {activeTab === 'evaluations' && (
          <div data-testid="evaluations-tab">
            {evalsLoading ? (
              <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
            ) : (
              <>
                {/* Stats summary */}
                {evalStats && evalStats.total > 0 && (
                  <div className="grid md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                      <p className="text-3xl font-black text-teal-700">{evalStats.total}</p>
                      <p className="text-xs text-slate-500 mt-1">Évaluations reçues</p>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                      <p className="text-3xl">{EMOJI_MAP[Math.round(evalStats.global_average)]}</p>
                      <p className="text-lg font-bold text-slate-800">{evalStats.global_average}/5</p>
                      <p className="text-xs text-slate-500">Moyenne globale</p>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                      <p className="text-3xl font-black text-green-600">
                        {evalStats.by_course.length > 0 ? evalStats.by_course[0].avg_overall : '-'}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">Meilleure note</p>
                      <p className="text-[10px] text-slate-400 truncate">{evalStats.by_course[0]?.course_title}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                      <p className="text-3xl font-black text-amber-600">{evalStats.by_course.length}</p>
                      <p className="text-xs text-slate-500 mt-1">Formations evaluees</p>
                    </div>
                  </div>
                )}

                {/* Per-course breakdown */}
                {evalStats?.by_course?.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 mb-6 overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-100">
                      <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4 text-teal-600" /> Scores par formation</h3>
                    </div>
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs text-slate-500">
                        <tr>
                          <th className="text-left p-3 font-medium">Formation</th>
                          <th className="text-center p-3 font-medium">Nb evals</th>
                          <th className="text-center p-3 font-medium">Satisfaction</th>
                          <th className="text-center p-3 font-medium">Contenu</th>
                          <th className="text-center p-3 font-medium">Clarte</th>
                          <th className="text-center p-3 font-medium">Applicabilite</th>
                          <th className="text-center p-3 font-medium">Moyenne</th>
                        </tr>
                      </thead>
                      <tbody>
                        {evalStats.by_course.map(cs => (
                          <tr key={cs.catalog_id} className="border-t border-slate-100">
                            <td className="p-3 font-medium text-slate-800">{cs.course_title}</td>
                            <td className="p-3 text-center text-slate-600">{cs.evaluation_count}</td>
                            <td className="p-3 text-center">{renderEmojiScore(cs.avg_satisfaction)} <span className="text-xs text-slate-500">{cs.avg_satisfaction}</span></td>
                            <td className="p-3 text-center">{renderEmojiScore(cs.avg_content)} <span className="text-xs text-slate-500">{cs.avg_content}</span></td>
                            <td className="p-3 text-center">{renderEmojiScore(cs.avg_clarity)} <span className="text-xs text-slate-500">{cs.avg_clarity}</span></td>
                            <td className="p-3 text-center">{renderEmojiScore(cs.avg_applicability)} <span className="text-xs text-slate-500">{cs.avg_applicability}</span></td>
                            <td className="p-3 text-center font-bold text-teal-700">{cs.avg_overall}/5</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Individual evaluations */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2"><MessageSquare className="w-4 h-4 text-teal-600" /> Évaluations individuelles</h3>
                  </div>
                  {evaluations.length === 0 ? (
                    <div className="text-center py-12">
                      <Star className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500 text-sm">Aucune évaluation pour le moment</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {evaluations.map(ev => (
                        <div key={ev.id} className="p-4" data-testid={`eval-${ev.id}`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-slate-900 text-sm">{ev.user_name} <span className="text-slate-400 font-normal">({ev.user_email})</span></p>
                              <p className="text-xs text-teal-600 mt-0.5">{ev.course_title}</p>
                            </div>
                            <div className="flex items-center gap-1">
                              {[ev.satisfaction, ev.content_quality, ev.clarity, ev.applicability].map((r, i) => (
                                <span key={i} className="text-lg">{EMOJI_MAP[r]}</span>
                              ))}
                              <span className="ml-2 text-sm font-bold text-teal-700">{ev.average_score}/5</span>
                            </div>
                          </div>
                          {ev.comment && (
                            <p className="mt-2 text-sm text-slate-600 bg-slate-50 rounded-lg p-3 italic">"{ev.comment}"</p>
                          )}
                          <p className="text-[10px] text-slate-400 mt-2">{new Date(ev.created_at).toLocaleString('fr-FR')}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* TAB: Blog */}
        {activeTab === 'blog' && (
          <AdminBlogTab token={token} />
        )}

        {/* TAB: Job Offers */}
        {activeTab === 'job-offers' && (
          <AdminJobOffersTab token={token} />
        )}

        {/* TAB: Espace Emploi */}
        {activeTab === 'espace-emploi' && (
          <AdminEspaceEmploiTab token={token} />
        )}

        {/* TAB: Paiements */}
        {activeTab === 'payments' && (
          <AdminPaymentsTab token={token} />
        )}

        {/* TAB: Réservations */}
        {activeTab === 'bookings' && (
          <AdminBookingsTab token={token} />
        )}

        {/* TAB: Site Content CMS */}
        {activeTab === 'site-content' && (
          <AdminSiteContentTab token={token} />
        )}

      </div>
    </div>
  );
};

export default AdminPage;
